import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";

import { suggestionPageQuery } from "./suggestionOptions";
import {
  type FetchSuggestionsProvider,
  matchSuggestions,
  type SuggestionPage,
  type SuggestionsProvider,
} from "./suggestions";

/**
 * Trailing debounce on typed text before a fetched source is asked again, so a
 * burst of typing collapses to one request. Matches the list pickers.
 */
const SEARCH_DEBOUNCE_MS = 300;

/** What a source offers for the text typed so far. */
export interface SuggestionPageState {
  /** The suggestions for the text, or undefined before any page has arrived. */
  page: SuggestionPage | undefined;
  /** Suggestions are being fetched; `page` may be an earlier one meanwhile. */
  isLoading: boolean;
  /** The source failed to answer for the text. */
  isError: boolean;
}

const NOTHING: SuggestionPageState = {
  page: undefined,
  isLoading: false,
  isError: false,
};

/**
 * Stands in for a missing fetch source, since hooks can't be skipped: both
 * queries are disabled whenever it is the source.
 */
const NO_FETCH: FetchSuggestionsProvider = {
  kind: "fetch",
  key: "none",
  fetch: () => Promise.resolve({ values: [] }),
};

/**
 * The suggestions `provider` offers for the text typed into a single value
 * field.
 *
 * - A `list` provider is matched on the client as the text changes.
 * - A `fetch` provider's page for the empty text is asked for at once, so it
 *   is ready when the field opens its suggestions. If that page is complete
 *   (not `truncated`), every later text narrows it on the client, with no
 *   request.
 *   Otherwise each text is fetched once typing settles (300 ms), keeping the
 *   previous page on screen meanwhile; text shorter than the provider's
 *   `minSearchLength` keeps the previous page without asking.
 *
 * Pages are read through `suggestionPageQuery`, so fields sharing a source
 * share pages.
 */
export function useSuggestionPage(
  provider: SuggestionsProvider | undefined,
  text: string,
): SuggestionPageState {
  const search = text.trim();
  const fetchProvider = provider?.kind === "fetch" ? provider : undefined;
  const source = fetchProvider ?? NO_FETCH;
  const minSearchLength = fetchProvider?.minSearchLength ?? 0;

  // Emptying the field settles at once and drops any pending search, so no
  // page is fetched for text that is gone, and the next search keeps the
  // empty-text page on screen rather than the last search's.
  const [settledSearch, setSettledSearch] = React.useState(search);
  const settle = useDebouncedCallback(setSettledSearch, SEARCH_DEBOUNCE_MS);
  React.useEffect(() => {
    if (search === "") {
      settle.cancel();
      setSettledSearch("");
    } else {
      settle(search);
    }
  }, [search, settle]);

  const firstPage = useQuery({
    ...suggestionPageQuery(source, ""),
    enabled: fetchProvider !== undefined,
  });
  const completePage =
    firstPage.data?.truncated === true ? undefined : firstPage.data;
  const searchedPage = useQuery({
    ...suggestionPageQuery(source, settledSearch),
    enabled:
      fetchProvider !== undefined &&
      completePage === undefined &&
      settledSearch !== "" &&
      settledSearch.length >= minSearchLength,
    placeholderData: keepPreviousData,
  });

  return React.useMemo((): SuggestionPageState => {
    if (provider === undefined) {
      return NOTHING;
    }
    if (provider.kind === "list") {
      return {
        page: {
          values: matchSuggestions(provider.values, search),
          counts: provider.counts,
        },
        isLoading: false,
        isError: false,
      };
    }
    if (completePage !== undefined) {
      return {
        page: {
          ...completePage,
          values: matchSuggestions(completePage.values, search),
        },
        isLoading: false,
        isError: false,
      };
    }
    if (search === "") {
      return {
        page: firstPage.data,
        isLoading: firstPage.isFetching,
        isError: firstPage.isError,
      };
    }
    return {
      page: searchedPage.data ?? firstPage.data,
      isLoading: searchedPage.isFetching,
      isError: searchedPage.isError,
    };
  }, [
    provider,
    search,
    completePage,
    firstPage.data,
    firstPage.isFetching,
    firstPage.isError,
    searchedPage.data,
    searchedPage.isFetching,
    searchedPage.isError,
  ]);
}
